import { CircleConfig } from '@calm/evexia-web-sdk';
import { useIsDarkMode } from '@rhg/hooks/use-provided-theme';
import { logger } from '@rhg/libs/logger';
import { ButtonBase } from '@rhg/libs/material';
import NextImage from '@rhg/libs/next/image';
import { routes } from '@rhg/utils/routes';
import { getSponsorLogoAlt } from '@rhg/utils/sponsor-logo-alt';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
    PARTNER_LOGO_HEIGHT,
    PARTNER_LOGO_HEIGHT_SMALL,
    PARTNER_LOGO_WIDTH,
} from './types';

interface PartnerImageProps {
    logoSrc: string;
    logoAlt?: string;
    size?: 'small' | 'large';
    /** Overrides the size-derived height (in px) while preserving aspect ratio. */
    height?: number;
}
function PartnerImage({
    size,
    logoAlt = '',
    logoSrc,
    height,
}: PartnerImageProps) {
    const logoHeight =
        height ??
        (size === 'small' ? PARTNER_LOGO_HEIGHT_SMALL : PARTNER_LOGO_HEIGHT);

    return (
        <NextImage
            width={0}
            height={logoHeight}
            sizes="100vw"
            style={{
                // `display: block` avoids the default inline image's
                // baseline-driven whitespace, which otherwise makes this
                // logo sit visibly higher than the height it renders at
                // (and its container taller than the image itself) when
                // placed next to another logo in a centered flex row.
                display: 'block',
                width: 'auto',
                maxWidth: PARTNER_LOGO_WIDTH,
                maxHeight: logoHeight,
                objectFit: 'contain',
            }}
            data-testid="LOGO"
            alt={logoAlt}
            src={logoSrc}
        />
    );
}

export interface PartnerConfigLogoProps {
    config?: CircleConfig;
    clickable?: boolean;
    size?: 'small' | 'large';
    /** Overrides the size-derived height (in px) while preserving aspect ratio. */
    height?: number;
    onClick?: () => void;
    partnerName?: string;
}
export const PartnerConfigLogo = ({
    config,
    onClick,
    size = 'large',
    height,
    clickable = true,
    partnerName,
}: PartnerConfigLogoProps) => {
    const { push } = useRouter();
    const isDarkMode = useIsDarkMode();
    const { formatMessage } = useIntl();

    const handleLogoClicked = () => {
        onClick?.();
        push(routes.dashboard).catch((err) =>
            logger.error('Failed to navigate to dashboard', {}, err as Error)
        );
    };

    const logoAlt = useMemo(
        () =>
            getSponsorLogoAlt(formatMessage, partnerName ?? '') ??
            config?.logo?.altText,
        [formatMessage, partnerName, config?.logo]
    );

    const logoSrc = useMemo(() => {
        if (isDarkMode) {
            return config?.logo?.darkUrl ?? '';
        }
        return config?.logo?.url ?? '';
    }, [isDarkMode, config?.logo?.darkUrl, config?.logo?.url]);

    if (!logoSrc) {
        return null;
    }

    return (
        <span data-testid="partner-logo">
            {clickable ? (
                <ButtonBase
                    onClick={handleLogoClicked}
                    trackButton="PartnerLogo"
                    aria-label={logoAlt}
                    tabIndex={0}
                >
                    <PartnerImage
                        logoAlt={logoAlt}
                        logoSrc={logoSrc}
                        size={size}
                        height={height}
                    />
                </ButtonBase>
            ) : (
                <PartnerImage
                    logoAlt={logoAlt}
                    logoSrc={logoSrc}
                    size={size}
                    height={height}
                />
            )}
        </span>
    );
};
